<template>
	<nta-dialog
		height="auto"
		width="580px"
		:show="compProps.show"
		:title="title"
		:symbol-lang="domainStore.languagePreference"
		@close="() => cancel()"
	>
		<template #actions>
			<v-btn @click="() => cancel()">{{ symbolsStore.GUILabel("4c03e40c-9b2d-4090-9eca-46a4f6fd2a4e") }}</v-btn>
			<v-btn
				color="success"
				:loading="loading"
				:disabled="!domainStore.hasAccessRights(UserRole.Editor) || !template || !label"
				@click="compProps.mode == 'edit' ? save() : create()"
				>{{
					compProps.mode == "create"
						? symbolsStore.GUILabel("59c65cfa-cd8d-47e2-ab59-003330041c4b", { label: "" })
						: symbolsStore.GUILabel("6c951dfc-4d19-4d3a-b89b-b45a88d11b3f")
				}}</v-btn
			>
		</template>
		<template v-if="domainStore.isAdmin && compProps.mode == 'edit'" #more-actions>
			<!-- <LanguageSelectorFlags :lang="lang" @select="(l: LanguageCode) => lang = l" /> -->
			<v-switch
				v-model="useMongoDB"
				:label="symbolsStore.GUILabel('1367f4f9-1c57-4fcc-aaac-da01988bc2bf')"
				hide-details
				@update:model-value="onMongoDBSwitchUpdate"
			></v-switch>
			<v-btn
				:disabled="!domainStore.hasAccessRights(UserRole.Editor)"
				color="error"
				prepend-icon="mdi-delete"
				@click="() => remove()"
			>
				{{
					symbolsStore.GUILabel("5cb8eb65-3c50-4152-9bb6-b610355cba12", {
						label: symbolsStore.GUILabel("1fa57246-ae3e-4cbe-b38d-da224bedc183"),
					})
				}}
			</v-btn>
		</template>
		<template #default>
			<v-card-text>
				<v-row>
					<v-col :cols="9">
						<v-autocomplete
							v-model="template"
							:disabled="compProps.mode === 'edit'"
							:items="templateDataspaces"
							:item-title="(item: any) => templateDataspaceTerm(item as TemplateDataspace)"
							:item-value="(item: any) => (item as TemplateDataspace).id"
							:label="
								symbolsStore.GUILabel('436fcee8-848b-447d-8979-cd1c98cc61c8', {
									label: symbolsStore.GUILabel('1fa57246-ae3e-4cbe-b38d-da224bedc183'),
								})
							"
						>
						</v-autocomplete>
					</v-col>
					<v-col :cols="3" class="d-flex align-center">
						<v-autocomplete v-model="version" :items="domainVersions" label="Version"> </v-autocomplete>
					</v-col>
				</v-row>
				<v-text-field
					v-model="label"
					:disabled="!domainStore.hasAccessRights(UserRole.Editor)"
					variant="outlined"
					:label="symbolsStore.GUILabel('20bfbe7b-c76e-46a5-ab88-16d53fb68945', { label: '' })"
				/>
				<v-text-field
					v-model="description"
					:disabled="!domainStore.hasAccessRights(UserRole.Editor)"
					variant="outlined"
					:label="symbolsStore.GUILabel('ceeb13a6-6e51-427d-9e2a-12249b8512a5')"
				/>

				<!-- Permissions -->
				<v-card class="mb-5">
					<v-card-text>
						<span style="font-size: medium">
							{{ symbolsStore.GUILabel("81bd9c01-30db-4a98-8970-e05452281012") }}</span
						>
						<div>
							<v-checkbox
								v-model="allowAllMembers"
								:disabled="!domainStore.isAdmin"
								:label="symbolsStore.GUILabel('7a2df6c1-5ca0-462d-acae-d20853244968')"
								hide-details
								@update:model-value="
										(newValue: boolean | null) => {
											if (newValue === null) return;
											if (newValue) {
												permittedUserIds = [];
											}
										}"
							/>
						</div>
						<v-select
							v-model="permittedUserIds"
							v-show="!allowAllMembers"
							:disabled="!domainStore.isAdmin || allowAllMembers"
							:items="selectableMembers"
							:item-title="(item: any) => `${item.firstname} ${item.lastname}`"
							:item-value="(item: any) => item.id"
							multiple
							chips
							closable-chips
							variant="outlined"
							hide-details
							:label="symbolsStore.GUILabel('b4d3ceaf-ec3b-4351-96bc-6a57d7706e6e')"
						>
							<template #chip="{ props, item }">
								<v-chip v-bind="props" :text="item.raw.firstname + ' ' + item.raw.lastname"> </v-chip>
							</template>
							<template #item="{ props, item }">
								<v-list-item v-bind="props">
									<template #prepend>
										<v-avatar :color="item.raw.color" size="32">
											<span
												:style="{
													color: isLight(item.raw.color) ? '#151618' : '#fafbfd',
													'font-weight': 500,
												}"
												>{{ getInitials(item.raw) }}</span
											>
										</v-avatar>
									</template>
								</v-list-item>
							</template>
						</v-select>
					</v-card-text>
				</v-card>

				<!-- Environment Variables -->
				<v-card v-if="domainStore.hasAccessRights(UserRole.Editor)">
					<v-card-text>
						<div class="d-flex justify-space-between align-center mb-2">
							<span style="font-size: medium">{{
								symbolsStore.GUILabel("d21aeb4d-b056-43fd-9714-3f9192b1b1a8")
							}}</span>
							<v-btn
								:disabled="!domainStore.isAdmin"
								class="ml-2"
								color="success"
								size="small"
								variant="tonal"
								@click="() => addEnvironmentVariable()"
								>{{ symbolsStore.GUILabel("1c81c103-627b-49e8-a7d1-030251061485") }}
							</v-btn>
						</div>

						<div style="max-height: 400px; overflow-y: auto">
							<v-expansion-panels v-model="expandedEnvPanel" variant="accordion" static>
								<v-expansion-panel
									v-for="(key, i) in environmentVariableKeys"
									:key="i"
									:value="i"
									:color="expandedEnvPanel === i ? '#e5e5e5' : undefined"
								>
									<v-expansion-panel-title>
										<div class="d-flex align-center justify-space-between" style="width: 100%">
											<span class="text-body-2 font-weight-medium text-truncate">{{
												environmentVariableKeys[i]
											}}</span>
											<v-btn
												v-if="domainStore.isAdmin"
												icon="mdi-delete"
												variant="text"
												density="compact"
												size="small"
												color="error"
												class="ml-2"
												@click.stop="
													() => {
														environmentVariableKeys.splice(i, 1);
														environmentVariableValues.splice(i, 1);
													}
												"
											></v-btn>
										</div>
									</v-expansion-panel-title>
									<v-expansion-panel-text>
										<v-combobox
											v-model="environmentVariableKeys[i]"
											:items="environmentVariableKeyOptions"
											:disabled="!domainStore.isAdmin"
											variant="outlined"
											density="compact"
											:label="symbolsStore.GUILabel('00fb7673-a3f9-4834-b7bd-066ecc4b61c9')"
											hide-details
											class="mt-2 mb-4"
											@update:model-value="
												(val) => {
													environmentVariableValues[i] =
														val === 'SUGGESTION_MODEL'
															? configStore.suggestionModels[0]
															: '';
												}
											"
										/>
										<v-text-field
											v-if="environmentVariablesMasked"
											v-model="environmentVariableValues[i]"
											:disabled="true"
											type="password"
											variant="outlined"
											density="compact"
											:label="symbolsStore.GUILabel('4081af65-fee7-4778-b9ea-ec6abd652119')"
											hide-details
										/>
										<v-select
											v-else-if="
												isLiveReaderAccount && environmentVariableKeys[i] === 'VOICE_BOT_TYPE'
											"
											v-model="environmentVariableValues[i]"
											:disabled="!domainStore.isAdmin"
											:items="voiceBotTypeOptions"
											variant="outlined"
											density="compact"
											:label="symbolsStore.GUILabel('4081af65-fee7-4778-b9ea-ec6abd652119')"
											hide-details
										></v-select>
										<v-combobox
											v-else-if="environmentVariableKeys[i] === 'SUGGESTION_MODEL'"
											v-model="environmentVariableValues[i]"
											:disabled="!domainStore.isAdmin"
											:items="configStore.suggestionModels"
											variant="outlined"
											density="compact"
											:label="symbolsStore.GUILabel('4081af65-fee7-4778-b9ea-ec6abd652119')"
											hide-details
										></v-combobox>
										<v-combobox
											v-else-if="environmentVariableKeys[i] === 'NTA_ALTERNIS_TOKEN'"
											v-model="environmentVariableValues[i]"
											:disabled="!domainStore.isAdmin"
											:items="['DEFAULT_NTA_ALTERNIS_TOKEN']"
											variant="outlined"
											density="compact"
											:label="symbolsStore.GUILabel('4081af65-fee7-4778-b9ea-ec6abd652119')"
											hide-details
										></v-combobox>
										<v-combobox
											v-else-if="environmentVariableKeys[i] === 'NTA_ALTERNIS_URL'"
											v-model="environmentVariableValues[i]"
											:disabled="!domainStore.isAdmin"
											:items="['DEFAULT_NTA_ALTERNIS_URL']"
											variant="outlined"
											density="compact"
											:label="symbolsStore.GUILabel('4081af65-fee7-4778-b9ea-ec6abd652119')"
											hide-details
										></v-combobox>
										<v-combobox
											v-else
											v-model="environmentVariableValues[i]"
											:disabled="!domainStore.isAdmin"
											variant="outlined"
											density="compact"
											:label="symbolsStore.GUILabel('4081af65-fee7-4778-b9ea-ec6abd652119')"
											hide-details
										/>
									</v-expansion-panel-text>
								</v-expansion-panel>
							</v-expansion-panels>
						</div>
					</v-card-text>
				</v-card>
			</v-card-text>
		</template>
	</nta-dialog>
	<ConfirmationModal
		:show="confirmationData.show"
		:message="confirmationData.message"
		@confirmation="confirmation"
		@done="() => (confirmationData.show = false)"
	/>
</template>

<script setup lang="ts">
import { TemplateDataspace, InstanceDataspace, LanguageCode } from "@lr-notitia/notitia-operator/schemas";
import { computed, ref, watch } from "vue";
import { useDomainStore } from "../../../store/domain";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { useModelGraphStore } from "../../../store/modelGraph";
import { useSymbolsStore } from "../../../store/symbols";
import { BotType } from "@lr-notitia/notitia-voice-bot/schemas";
import { useConfigStore } from "../../../store/alternis/config";

// import LanguageSelectorFlags from "../../../components/LanguageSelectorFlags.vue";
import ConfirmationModal from "../../../components/ConfirmationModal.vue";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import { api } from "@lr-notitia/notitia-operator/client";

const domainStore = useDomainStore();
const modelStore = useModelGraphStore();
const instanceDataspacesStore = useInstanceDataspacesStore();
const symbolsStore = useSymbolsStore();
const configStore = useConfigStore();

const title = computed(() =>
	compProps.mode == "edit"
		? symbolsStore.GUILabel("a2c902fa-c61f-4bf0-98ce-e50535d4826a", {
				label: symbolsStore.GUILabel("1fa57246-ae3e-4cbe-b38d-da224bedc183"),
		  })
		: symbolsStore.GUILabel("59c65cfa-cd8d-47e2-ab59-003330041c4b", {
				label: symbolsStore.GUILabel("1fa57246-ae3e-4cbe-b38d-da224bedc183"),
		  }),
);
const templateDataspaces = computed(() => {
	return Object.values(modelStore.graph.nodes).filter((n) => n.layer === "template" && n.type === "dataspace");
});
const domainVersions = computed(() => {
	let versions = [...domainStore.domain.versions];
	if (!versions.includes("wip")) versions.push("wip");
	return versions.reverse();
});

const lang = ref<LanguageCode>(domainStore.languagePreference);
const confirmationData = ref<{ show: boolean; message: string }>({
	show: false,
	message: "",
});

const compProps = defineProps<{
	show: boolean;
	mode: "create" | "edit";
	dataspace?: InstanceDataspace;
}>();
const emits = defineEmits(["done", "cancel"]);

const template = ref<string>("");
const version = ref<string>("wip");
const label = ref<string>("");
const description = ref<string>("");
const workspaces = ref<string[]>([]);
const loading = ref<boolean>(false);

const mongoDbRegisteredDataspaces = ref<string[]>([]);
const useMongoDB = ref<boolean>(false);

const permittedUserIds = ref<string[]>([]);
const allowAllMembers = ref<boolean>(false);

const selectableMembers = computed(() => {
	return domainStore.members.filter((member) => member.role !== UserRole.Admin);
});

const isLiveReaderAccount = computed(() => domainStore.account.email?.includes("@livereader.") ?? false);
const environmentVariableKeyOptions = computed(() => {
	const keys = ["NTA_ALTERNIS_URL", "NTA_ALTERNIS_TOKEN", "SHOW_UNDO_BUTTON"];
	if (domainStore.isAdmin) keys.push("SUGGESTION_MODEL");
	if (isLiveReaderAccount.value) keys.push("VOICE_BOT_TYPE");
	return keys;
});
const voiceBotTypeOptions = computed(() => Object.values(BotType) as string[]);

function getInitials(user: any) {
	return (user.firstname?.charAt(0) || "") + (user.lastname?.charAt(0) || "");
}

function remove() {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	if (!compProps.dataspace) return;
	confirmationData.value.show = true;
	confirmationData.value.message = 'Are you sure you want to delete dataspace "' + compProps.dataspace.label + '"?';
}
function confirmation(confirmed: boolean) {
	if (!confirmed) return;
	if (!compProps.dataspace) return;
	instanceDataspacesStore.removeDataspace(compProps.dataspace.id);
	emits("done");
}

function create() {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	if (!label.value) return;
	loading.value = true;
	instanceDataspacesStore.addDataspace(template.value, version.value, label.value, description.value).then((d) => {
		if (!d.dataspace) return;
		writeEnvironmentVariables(d.dataspace.id);
		savePermittedUsers(d.dataspace.id);
		loading.value = false;
		emits("done");
	});
}
function save() {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	if (!compProps.dataspace) return;
	loading.value = true;
	instanceDataspacesStore
		.updateDataspace({
			...compProps.dataspace,
			template: template.value,
			version: version.value,
			label: label.value,
			description: description.value,
			workspaces: workspaces.value,
		})
		.then((d) => {
			writeEnvironmentVariables(d.id);
			savePermittedUsers(d.id);
			loading.value = false;
			emits("done");
		});
}
function cancel() {
	emits("cancel");
}

function templateDataspaceTerm(dataspace: TemplateDataspace) {
	return symbolsStore.term({ id: dataspace.refersTo, terminology: dataspace.terminology, lang: lang.value });
}

function onMongoDBSwitchUpdate(newValue: boolean | null) {
	if (newValue === null) return;
	useMongoDB.value = newValue;
	if (newValue) {
		// Register the dataspace
		if (compProps.dataspace?.id) {
			instanceDataspacesStore
				.mongoDbRegisterDataspace(domainStore.domain.id, compProps.dataspace.id)
				.catch((err) => console.error("Error registering dataspace:", err));
		}
	} else {
		// Unregister the dataspace
		if (compProps.dataspace?.id) {
			instanceDataspacesStore
				.mongoDbUnregisterDataspace(domainStore.domain.id, compProps.dataspace.id)
				.catch((err) => console.error("Error unregistering dataspace:", err));
		}
	}
}

watch(
	() => compProps.show,
	() => {
		if (!compProps.show) return;

		instanceDataspacesStore.mongoDbgetRegisteredDataspaces(domainStore.domain.id).then((d) => {
			mongoDbRegisteredDataspaces.value = d;
			if (compProps.dataspace)
				useMongoDB.value = mongoDbRegisteredDataspaces.value.includes(compProps.dataspace.id);
		});

		lang.value = domainStore.languagePreference;
		if (compProps.mode == "create") {
			template.value = "";
			version.value = domainStore.domain.versions.length
				? domainStore.domain.versions[domainStore.domain.versions.length - 1]
				: "wip";
			label.value = "";
			description.value = "";
			workspaces.value = [];
			permittedUserIds.value = [];
			allowAllMembers.value = true; // Default for new dataspaces
			environmentVariableKeys.value = ["NTA_ALTERNIS_URL", "NTA_ALTERNIS_TOKEN"];
			environmentVariableValues.value = ["DEFAULT_NTA_ALTERNIS_URL", "DEFAULT_NTA_ALTERNIS_TOKEN"];
		} else {
			template.value = compProps.dataspace?.template || "";
			version.value = compProps.dataspace?.version || "wip";
			label.value = compProps.dataspace?.label || "";
			description.value = compProps.dataspace?.description || "";
			workspaces.value = compProps.dataspace?.workspaces || [];
			loadPermittedUsers();
		}
	},
	{ immediate: true },
);

const environmentVariableKeys = ref<string[]>([]);
const environmentVariableValues = ref<string[]>([]);
const environmentVariablesMasked = ref<boolean>(false);
const expandedEnvPanel = ref<number | undefined>(undefined);

watch(
	() => compProps.show,
	() => {
		if (!compProps.show) return;
		loadEnvironmentVariables();
	},
	{ immediate: true },
);

function addEnvironmentVariable() {
	environmentVariableKeys.value.push(symbolsStore.GUILabel("00fb7673-a3f9-4834-b7bd-066ecc4b61c9").toUpperCase());
	environmentVariableValues.value.push(symbolsStore.GUILabel("4081af65-fee7-4778-b9ea-ec6abd652119").toUpperCase());
}
function loadEnvironmentVariables() {
	if (!compProps.dataspace) return;
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	const mask = !domainStore.hasAccessRights(UserRole.Admin);
	environmentVariablesMasked.value = mask;
	api()
		.domain(domainStore.domain.id)
		.dataspace(compProps.dataspace.id)
		.environment()
		.getEnvironmentVariables({ mask })
		.then((data) => {
			environmentVariableKeys.value = Object.keys(data);
			environmentVariableValues.value = Object.values(data);
		})
		.catch((error) => {
			console.error("Error fetching environment variables:", error);
		});
}
function writeEnvironmentVariables(dataspaceId: string) {
	const envVars: { [key: string]: string } = {};

	// Keep entries only where both key and value are truthy
	const validIndices = environmentVariableKeys.value
		.map((_, index) => index)
		.filter((index) => environmentVariableKeys.value[index] && environmentVariableValues.value[index]);

	// Create environment variables object from valid pairs
	validIndices.forEach((index) => {
		envVars[environmentVariableKeys.value[index]] = environmentVariableValues.value[index];
	});

	api()
		.domain(domainStore.domain.id)
		.dataspace(dataspaceId)
		.environment()
		.setEnvironmentVariables(envVars)
		.catch((error) => {
			console.error("Error saving environment variables:", error);
		});
}

async function loadPermittedUsers() {
	if (!compProps.dataspace) return;
	try {
		const allowAll = await instanceDataspacesStore.getAllowAll(domainStore.domain.id, compProps.dataspace.id);
		allowAllMembers.value = allowAll;

		const userIds = await instanceDataspacesStore.getPermittedUserIds(
			domainStore.domain.id,
			compProps.dataspace.id,
		);
		permittedUserIds.value = userIds;
	} catch (error) {
		console.error("Error loading permitted users:", error);
		permittedUserIds.value = [];
		allowAllMembers.value = true; // Default to allow all
	}
}

async function savePermittedUsers(dataspaceId: string) {
	if (!compProps.dataspace && compProps.mode === "edit") return;
	try {
		// First set the allow all members flag
		await instanceDataspacesStore.setAllowAll(domainStore.domain.id, dataspaceId, allowAllMembers.value);

		// Only update individual permissions if not allowing all
		if (!allowAllMembers.value) {
			const currentUserIds = await instanceDataspacesStore.getPermittedUserIds(
				domainStore.domain.id,
				dataspaceId,
			);

			const usersToAdd = permittedUserIds.value.filter((id) => !currentUserIds.includes(id));
			const usersToRemove = currentUserIds.filter((id) => !permittedUserIds.value.includes(id));

			for (const userId of usersToAdd) {
				await instanceDataspacesStore.addPermittedUserId(domainStore.domain.id, dataspaceId, userId);
			}
			for (const userId of usersToRemove) {
				await instanceDataspacesStore.removePermittedUserId(domainStore.domain.id, dataspaceId, userId);
			}
		}
	} catch (error) {
		console.error("Error saving permitted users:", error);
	}
}

function brightness(color: string) {
	if (!color) return -1;
	const isHEX = color.indexOf("#") == 0;
	const isRGB = color.indexOf("rgb") == 0;
	if (isHEX) {
		const hasFullHex = color.length == 7;
		const r = hasFullHex ? parseInt(color.substring(1, 3), 16) : parseInt(color.substring(1, 2), 16);
		const g = hasFullHex ? parseInt(color.substring(3, 5), 16) : parseInt(color.substring(2, 3), 16);
		const b = hasFullHex ? parseInt(color.substring(5, 7), 16) : parseInt(color.substring(3, 4), 16);
		return (r * 299 + g * 587 + b * 114) / 1000 / 255;
	} else if (isRGB) {
		const r = parseInt(color.substring(5, 7), 16);
		const g = parseInt(color.substring(7, 9), 16);
		const b = parseInt(color.substring(9, 11), 16);
		return (r * 299 + g * 587 + b * 114) / 1000 / 255;
	}
	return -1;
}
function isLight(color: string) {
	return brightness(color) > 0.5;
}
</script>
